import { useEffect, useState, useRef } from "react";
import Pay, { CheckoutObject, ValidationResponse } from "@pay-com/js";

const PayComIFrame = ({
  merchantId,
  paymentSessionId,
  isProductionEnvironment,
  cssClass,
  shouldSubmitPayment, // A flag to trigger submit payment method
  shouldResync, // A flag to trigger resync method
  shouldUseHeadlessMode, // A flag to set payment in headless mode
  shouldSubmitApplyPayPayment, // A flag to trigger submit Apple pay method
  shouldSubmitGooglePayPayment, // A flag to trigger submit Google pay method
  shouldSubmitPayPalPayment, // A flag to trigger submit Pay Pal method
  shouldSubmitMobilePayment, // A flag to trigger submit Mobile pay method
  shouldSubmitBankAccountPayment, // A flag to trigger submit Bank Account pay method
  shouldSubmitTrustlyPayment, // A flag to trigger submit Trustly method
  onGetPaymentMethodsResultCallback, // A callback after getting payments method active
  onCanMakePaymentResultCallback, // A callback after checking platform capability for payments
  onPaymentBeforeInitCallback, // Called before pay.com starts initialsing
  onPaymentContentReadyCallback, // Called when pay.com has been initialised successfully
  onPaymentProcessingCallback, // Called when payment is submitting
  onPaymentSuccessfulCallback, // Called when payment result is successful
  onPaymentFailedCallback, // Called when payment result is failed
  onPaymentResyncCallback, // Called when resync if finished
  onPaymentValidationCallback, // Called after payment form validation is done
}: {
  merchantId: string;
  paymentSessionId: string;
  isProductionEnvironment: boolean;
  cssClass?: string;
  shouldSubmitPayment?: boolean;
  shouldResync?: boolean;
  shouldUseHeadlessMode: boolean;
  shouldSubmitApplyPayPayment: boolean;
  shouldSubmitGooglePayPayment: boolean;
  shouldSubmitPayPalPayment: boolean;
  shouldSubmitMobilePayment: boolean;
  shouldSubmitBankAccountPayment: boolean;
  shouldSubmitTrustlyPayment?: boolean;
  onCanMakePaymentResultCallback?: (result: any) => void;
  onGetPaymentMethodsResultCallback?: (result: any) => void;
  onPaymentBeforeInitCallback?: () => void;
  onPaymentContentReadyCallback?: (result: any) => void;
  onPaymentProcessingCallback?: (result: any) => void;
  onPaymentSuccessfulCallback?: (result: any) => void;
  onPaymentFailedCallback?: (result: any) => void;
  onPaymentResyncCallback?: (result: any) => void;
  onPaymentValidationCallback?: (result: any) => void;
}) => {
  const payComRef = useRef<Awaited<ReturnType<typeof Pay.com>>>(null);
  const payComCheckout = useRef<CheckoutObject>(null);
  const [shouldRenderPayComIframe, setShouldRenderPayComIframe] = useState<boolean>(false);
  const doLogging = false;

  useEffect(() => {
    createPayComIFrame();
  }, []);

  useEffect(() => {
    if (shouldSubmitPayment) {
      handleSubmitPayment();
      return;
    }

    if (shouldSubmitApplyPayPayment) {
      handleApplyPaySubmit();
      return;
    }

    if (shouldSubmitGooglePayPayment) {
      handleGooglePaySubmit();
      return;
    }

    if (shouldSubmitTrustlyPayment) {
      handleTrustlyPaySubmit();
      return;
    }

    if (shouldSubmitPayPalPayment) {
      handlePaypalSubmit();
      return;
    }

    if (shouldSubmitMobilePayment) {
      handleMobilePaySubmit();
      return;
    }

    if (shouldSubmitBankAccountPayment) {
      handleBankAccountPaySubmit();
      return;
    }
  }, [
    shouldSubmitPayment,
    shouldSubmitApplyPayPayment,
    shouldSubmitGooglePayPayment,
    shouldSubmitTrustlyPayment,
    shouldSubmitPayPalPayment,
    shouldSubmitMobilePayment,
    shouldSubmitBankAccountPayment,
  ]);

  useEffect(() => {
    if (shouldResync) {
      handleResync();
    }
  }, [shouldResync]);

  const createPayComIFrame = async () => {
    try {
      if (!merchantId || !paymentSessionId) {
        console.log("Invalid merchant id");
        return;
      }

      if (!paymentSessionId) {
        console.log("Invalid paymentSessionId");
        return;
      }

      if (onPaymentBeforeInitCallback) {
        onPaymentBeforeInitCallback();
      }

      initializePayComSDK(paymentSessionId);
    } catch (error) {
      console.error(`Error in createPayComIFrame - ${JSON.stringify(error)}`);
    } finally {
    }
  };

  const initializePayComSDK = async (paymentSessionId: string) => {
    try {
      setShouldRenderPayComIframe(true);

      payComRef.current = await Pay.com({
        identifier: merchantId,
        live: isProductionEnvironment,
      });

      payComCheckout.current = payComRef.current.checkout({
        token: paymentSessionId,
      });

      // Handle payment capabilities
      payComCheckout.current.canMakePayments().then(function (data) {
        if (doLogging) {
          console.log("Pay.Com Event = canMakePayments");
          console.log(data);
        }

        if (onCanMakePaymentResultCallback) {
          onCanMakePaymentResultCallback(data);
        }
      });

      // get active methods
      payComCheckout.current.getPaymentMethods().then(function (data) {
        if (doLogging) {
          console.log("Pay.Com Event = getPaymentMethods");
          console.log(data);
        }

        if (onGetPaymentMethodsResultCallback) {
          onGetPaymentMethodsResultCallback(data);
        }
      });

      // Handle content ready
      payComCheckout.current.on(payComCheckout.current.EVENT_TYPES.CONTENT_READY, data => {
        if (doLogging) {
          console.log("Pay.Com Event = content ready");
          console.log(data);
        }

        if (onPaymentContentReadyCallback) {
          onPaymentContentReadyCallback(data);
        }
      });

      // Handle payment processing
      payComCheckout.current.on(payComCheckout.current.EVENT_TYPES.PAYMENT_PROCESSING, data => {
        if (doLogging) {
          console.log(">>>> Pay.Com Event = payment processing");
          console.log(data);
        }

        if (onPaymentProcessingCallback) {
          onPaymentProcessingCallback(data);
        }
      });

      // Handle payment success
      payComCheckout.current.on(payComCheckout.current.EVENT_TYPES.PAYMENT_SUCCESS, data => {
        if (doLogging) {
          console.log(">>>> Pay.Com Event = payment success");
          console.log(data);
        }

        if (onPaymentSuccessfulCallback) {
          onPaymentSuccessfulCallback(data);
        }

        setShouldRenderPayComIframe(false);
      });

      // Handle payment failure
      payComCheckout.current.on(payComCheckout.current.EVENT_TYPES.PAYMENT_FAILURE, data => {
        if (onPaymentFailedCallback) {
          onPaymentFailedCallback(data);
        }
      });

      // Options
      if (shouldUseHeadlessMode) {
        payComCheckout.current.paypal({
          container: "#paypal-button",
          onClickValidation: async () => {
            return true;
          }
        });

        await payComCheckout.current.universal({
          container: "#paycom-iframe-container",
          toggles: {
            submitButton: false,
          },
          style: {
            cardForm: {
              base: {},
              number: {},
              cvv: {},
              expiry: {},
              name: {},
            },
            base: {},
            savedPaymentMethods: {},
            expressCheckout: {},
          },
          paymentMethods: ["card"],
        });
      } else {
        await payComCheckout.current.universal({
          container: "#paycom-iframe-container",
          toggles: {
            submitButton: false,
          },
          style: {
            cardForm: {
              base: {},
              number: {},
              cvv: {},
              expiry: {},
              name: {},
            },
            base: {},
            savedPaymentMethods: {},
            expressCheckout: {},
          },
        });
      }
    } catch (error) {
      console.error(`Error in initializePayComSDK - ${JSON.stringify(error)}`);
    }
  };

  const handleSubmitPayment = async () => {
    let isValid = false;

    await payComCheckout.current.validate().then(async response => {
      const result = response as ValidationResponse;
      isValid = result.valid;

      let paymentResult = null;

      if (isValid) {
        paymentResult = await payComCheckout.current.submit();
      }

      if (onPaymentValidationCallback) {
        onPaymentValidationCallback(isValid);
      }
    });
  };

  const handleResync = async () => {
    if (payComCheckout?.current) {
      await payComCheckout.current.resync().then(response => {
        if (onPaymentResyncCallback) {
          onPaymentResyncCallback(response);
        }
      });
    } else {
      console.log("Resync failed. payComCheckout is null");
    }
  };

  const handleApplyPaySubmit = async () => {
    await payComCheckout.current.headless("applepay").then(function (response) {
      console.log("Pay.Com Apple Pay response");
      console.log(response);
    });
  };

  const handleGooglePaySubmit = async () => {
    await payComCheckout.current.headless("googlepay").then(function (response) {
      console.log("Pay.Com Google Pay response");
      console.log(response);
    });
  };

  const handleTrustlyPaySubmit = async () => {
    await payComCheckout.current.headless("trustly").then(function (response) {
      console.log("Pay.Com trustly response");
      console.log(response);
    });
  };

  const handlePaypalSubmit = async () => {
    await payComCheckout.current.headless("paypal").then(function (response) {
      console.log("Pay.Com Pay Pal response");
      console.log(response);
    });
  };

  const handleMobilePaySubmit = async () => {
    await payComCheckout.current.headless("mobile").then(function (response) {
      console.log("Pay.Com Mobile Pay response");
      console.log(response);
    });
  };

  const handleBankAccountPaySubmit = async () => {
    await payComCheckout.current.headless("bank_account").then(function (response) {
      console.log("Pay.Com Bank Account Pay response");
      console.log(response);
    });
  };

  return (
    <>
      {shouldRenderPayComIframe && (
        <>
          <div id="paycom-iframe-container" className={cssClass}></div>
        </>
      )}
    </>
  );
};

export default PayComIFrame;
